import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon, CheckIcon, XMarkIcon,
  CircleStackIcon, ServerIcon, CpuChipIcon, SignalIcon, Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import ContextTabLink from './common/ContextTabLink';
import CiSelector from './CiSelector';
import InstanceDatabasesPanel from './InstanceDatabasesPanel';
import ServiceOverviewMap from './ServiceOverviewMap';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const EMPTY = {
  name: '',
  service_id: '',
  host_server_id: '',
  host_vm_id: '',
  dbms: 'postgres',
  version: '',
  port: '',
  data_dir: '',
  role: 'standalone',
  status: 'online',
  notes: '',
  copy_method: '',
  replication_target: '',
  replication_config: '',
  replication_scripts: '',
};

export default function DatabaseInstanceForm({ instance, companyId, meta = {}, services = [], onClose, onSaved, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);
  const isEdit = Boolean(instance?.id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState(instance?.id && initialMode === 'view' ? 'resumen' : 'motor');
  // Bases alojadas dentro de la instancia: se refresca desde su pestaña.
  const [dbCount, setDbCount] = useState(instance?.databases_count ?? 0);

  useEffect(() => {
    setDbCount(instance?.databases_count ?? 0);
  }, [instance]);

  useEffect(() => {
    if (instance?.id && initialMode === 'view') setActiveTab('resumen');
  }, [instance?.id, initialMode]);

  useEffect(() => {
    if (instance) {
      setForm({
        name: instance.name || '',
        service_id: instance.service_id || '',
        host_server_id: instance.host_server_id || '',
        host_vm_id: instance.host_vm_id || '',
        dbms: instance.dbms || 'postgres',
        version: instance.version || '',
        port: instance.port ?? '',
        data_dir: instance.data_dir || '',
        role: instance.role || 'standalone',
        status: instance.status || 'online',
        notes: instance.notes || '',
        copy_method: instance.copy_method || '',
        replication_target: instance.replication_target || '',
        replication_config: instance.replication_config || '',
        replication_scripts: instance.replication_scripts || '',
      });
    } else {
      setForm(EMPTY);
    }
    setIsDirty(false);
  }, [instance, reloadKey]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
    setError('');
  };

  const handleHostServerSelect = (ci) => { setForm(prev => ({ ...prev, host_server_id: ci.ci_id })); setIsDirty(true); };
  const handleHostVmSelect = (ci) => { setForm(prev => ({ ...prev, host_vm_id: ci.ci_id })); setIsDirty(true); };

  /* Descartar: recarga los valores guardados y sale de edición. */
  const handleDiscard = () => {
    setReloadKey(k => k + 1);
    setIsDirty(false);
    setError('');
    if (isEdit) setEditing(false);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      // El servicio es opcional: la BD puede depender solo de la máquina.
      payload.service_id = payload.service_id ? Number(payload.service_id) : null;
      payload.host_server_id = payload.host_server_id ? Number(payload.host_server_id) : null;
      payload.host_vm_id = payload.host_vm_id ? Number(payload.host_vm_id) : null;
      payload.port = payload.port === '' ? null : Number(payload.port);
      if (isEdit) {
        await api.updateDatabaseInstance(instance.id, payload, companyId);
        // Se guarda sin salir: la ficha se queda abierta (solo se refresca el listado).
        onSaved?.(null);
      } else {
        // Tras crear, mantener el formulario abierto en modo edición para
        // poder seguir completando secciones (máquina, replicación…) sin salir.
        const created = registroGuardado(await api.createDatabaseInstance(payload, companyId));
        onSaved?.(created);
      }
      setIsDirty(false);
      return true;
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
      return false;
    } finally { setSaving(false); }
  };

  const serviceLabel = (id) =>
    services.find(s => s.id === Number(id))?.display_name ||
    services.find(s => s.id === Number(id))?.name || id;

  const currentService = form.service_id ? serviceLabel(form.service_id) : '—';
  const hostName = form.host_server_id
    ? (String(form.host_server_id) === String(instance?.host_server_id) ? (instance?.host_server_name || `Servidor #${form.host_server_id}`) : `Servidor #${form.host_server_id}`)
    : (form.host_vm_id
      ? (String(form.host_vm_id) === String(instance?.host_vm_id) ? (instance?.host_vm_name || `VM #${form.host_vm_id}`) : `VM #${form.host_vm_id}`)
      : '');
  const hostRoute = form.host_server_id
    ? `/infrastructure/servers?destacar=${form.host_server_id}`
    : (form.host_vm_id ? `/virt/vms?destacar=${form.host_vm_id}` : null);
  const statusLabel = (s) => ({ online: 'Online', offline: 'Fuera de línea', maintenance: 'Mantenimiento' }[s] || s || '—');
  const roleLabel = (v) => ({ primary: 'Primario', replica: 'Réplica', standalone: 'Standalone', witness: 'Testigo' }[v] || v || '—');
  const dbmsLabel = (v) => ({ postgres: 'PostgreSQL', mysql: 'MySQL', mssql: 'SQL Server', oracle: 'Oracle', mongodb: 'MongoDB', redis: 'Redis' }[v] || v || '—');
  const dbLabel = `${dbmsLabel(form.dbms)}${form.version ? ` ${form.version}` : ''}`;

  // En tarjetas, «Host / Ubicación» se edita dentro del Resumen.
  const tabsNav = (
    <div className="dv-tabs-nav">
      {isEdit && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'resumen' ? ' active' : ''}`} onClick={() => setActiveTab('resumen')}>Resumen</TabButton>
      )}
      <TabButton type="button" className={`dv-tab-btn${activeTab === 'motor' ? ' active' : ''}`} onClick={() => setActiveTab('motor')}>Motor de BD</TabButton>
      {isEdit && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'bases' ? ' active' : ''}`} onClick={() => setActiveTab('bases')}>Bases de datos ({dbCount})</TabButton>
      )}
      {!isCards && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'host' ? ' active' : ''}`} onClick={() => setActiveTab('host')}>Host / Ubicación</TabButton>
      )}
      {isEdit && form.service_id && (
        <TabButton type="button" className={`dv-tab-btn${activeTab === 'dependencias' ? ' active' : ''}`} onClick={() => setActiveTab('dependencias')}>Dependencias</TabButton>
      )}
    </div>
  );
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon />
          Bases de datos
        </button>
        <DetailCrumb label={form.name || form.dbms || 'Nueva'} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Instancia"
          disabled={saving}
          onChange={(destino) => {
            if (!editing || window.confirm('Hay una edición abierta. ¿Descartar los cambios sin guardar y pasar a la siguiente instancia?')) {
              onNavigate?.(destino);
            }
          }}
        />
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon />
              Cerrar
            </button>
          ) : !editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon />
                Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => { setEditing(true); setActiveTab('motor'); }}>
                <CheckIcon />
                Editar
              </button>
            </>
          ) : (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose} disabled={saving}>
                <XMarkIcon />
                Descartar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* SmartBar (solo en la ficha clásica) */}
      {!isCards && (
      <div className="dv-smartbar">
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('motor')}>
          <div className="dv-smart-btn-icon"><CircleStackIcon /></div>
          <div className="dv-smart-btn-value">{dbmsLabel(form.dbms)}</div>
          <div className="dv-smart-btn-label">DBMS</div>
        </button>
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('motor')}>
          <div className="dv-smart-btn-icon"><ServerIcon /></div>
          <div className="dv-smart-btn-value">{form.port || '—'}</div>
          <div className="dv-smart-btn-label">Puerto</div>
        </button>
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('motor')}>
          <div className="dv-smart-btn-icon"><CpuChipIcon /></div>
          <div className="dv-smart-btn-value">{roleLabel(form.role)}</div>
          <div className="dv-smart-btn-label">Rol</div>
        </button>
        <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('motor')}>
          <div className="dv-smart-btn-icon"><SignalIcon /></div>
          <div className="dv-smart-btn-value">{statusLabel(form.status)}</div>
          <div className="dv-smart-btn-label">Estado</div>
        </button>
        {isEdit && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('bases')}>
            <div className="dv-smart-btn-icon"><CircleStackIcon /></div>
            <div className="dv-smart-btn-value">{dbCount}</div>
            <div className="dv-smart-btn-label">Bases dentro</div>
          </button>
        )}
        {isEdit && form.service_id && (
          <button type="button" className="dv-smart-btn" onClick={() => setActiveTab('dependencias')}
            title="Servicio y dependencias de la instancia">
            <div className="dv-smart-btn-icon"><Squares2X2Icon /></div>
            <div className="dv-smart-btn-value">🔗</div>
            <div className="dv-smart-btn-label">Dependencias</div>
          </button>
        )}

        {isEdit && (
          <DocSmartButton elementType="database" elementId={instance.id} elementName={form.name || dbLabel} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

        <FormCardsShell
          isCards={isCards}
          header={isCards ? (
            <FormCardsHead
              icon={<CircleStackIcon style={{ width: 20, height: 20 }} />}
              title={form.name || dbLabel}
              onTitleChange={(v) => { setForm(prev => ({ ...prev, name: v })); setIsDirty(true); }}
              placeholder="Nombre de la instancia…"
              editing={editing}
              saving={saving}
              onSave={handleSave}
              onCancel={onClose}
              onEdit={() => setEditing(true)}
              crumb={[dbLabel, currentService, form.host_name].filter(Boolean).join(' › ')}
              badges={[
                { text: statusLabel(form.status), color: form.status === 'online' ? '#16a34a' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                { text: roleLabel(form.role) },
              ]}
              chips={[
                { value: form.port || '—', label: 'puerto', onClick: () => setActiveTab('motor') },
                { value: dbCount, label: 'bases', onClick: () => setActiveTab('bases') },
              ]}
              extra={isEdit ? (
                <DocSmartButton elementType="database" elementId={instance.id} elementName={form.name || dbLabel} companyId={companyId} variant="chip" />
              ) : null}
            />
          ) : null}
          tabs={isCards ? tabsNav : null}
        >
        <fieldset className="dv-fieldset" disabled={!editing}>
        <form className="dv-form" onSubmit={handleSave}>
          {!isCards && tabsNav}

          {activeTab === 'resumen' && isEdit && (
            <div className={paneClass}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Motor</span><b style={{ fontSize: '0.95rem' }}>{dbLabel}</b></div>
                  <div className="sf2-kpi"><span>Puerto</span><b>{form.port || '—'}</b></div>
                  <div className="sf2-kpi"><span>Rol</span><b style={{ fontSize: '0.95rem' }}>{roleLabel(form.role)}</b></div>
                  <div className="sf2-kpi"><span>Bases</span><b>{dbCount}</b></div>
                  <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusLabel(form.status)}</b></div>
                </div>
              )}
              <div className="dv-context-grid">
                <div className="dv-context-card">
                  <div className="dv-context-icon">🧩</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Servicio</div>
                    <div className="dv-context-title">{currentService}</div>
                    <div className="dv-context-sub">{form.service_id ? 'Servicio al que pertenece la instancia' : 'Sin servicio: solo depende de la máquina'}</div>
                    {form.service_id && (
                      <div className="dv-context-actions">
                        <Link to={`/services?destacar=${form.service_id}&abrir=1`} className="dv-context-link">Abrir servicio ↗</Link>
                        <Link to={`/services/designer?service=${form.service_id}`} className="dv-context-link">Diseñador ↗</Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">🖥️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Host</div>
                    <div className="dv-context-title">{hostName || 'Sin host enlazado'}</div>
                    <div className="dv-context-sub">{form.data_dir || 'Sin data directory'}</div>
                    {hostRoute && (
                      <div className="dv-context-actions">
                        <Link to={hostRoute} className="dv-context-link">Abrir host ↗</Link>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dv-context-card">
                  <div className="dv-context-icon">🗄️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Motor</div>
                    <div className="dv-context-title">{dbLabel}</div>
                    <div className="dv-context-sub">Puerto {form.port || '—'} · rol {form.role || '—'} · {statusLabel(form.status)}</div>
                    <div className="dv-context-actions">
                      <ContextTabLink tab="motor" onSelect={setActiveTab}>Ver configuración</ContextTabLink>
                    </div>
                  </div>
                </div>

                <div className="dv-context-card dv-context-card-warn">
                  <div className="dv-context-icon">⚠️</div>
                  <div className="dv-context-body">
                    <div className="dv-context-kicker">Impacto</div>
                    <div className="dv-context-title">{dbCount} base{dbCount === 1 ? '' : 's'} dentro</div>
                    <div className="dv-context-sub">Si la instancia cae, {currentService !== '—' ? currentService : 'su servicio'} y sus bases quedan afectados.</div>
                    <div className="dv-context-actions">
                      <ContextTabLink tab="bases" onSelect={setActiveTab}>Ver bases</ContextTabLink>
                      {form.service_id && <ContextTabLink tab="dependencias" onSelect={setActiveTab}>Ver dependencias ↗</ContextTabLink>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Resumen de la instancia</div>
                <div className="dv-summary-grid">
                  <div><span>Motor</span><strong>{dbLabel}</strong></div>
                  <div><span>Puerto</span><strong>{form.port || '—'}</strong></div>
                  <div><span>Rol</span><strong>{form.role || '—'}</strong></div>
                  <div><span>Bases dentro</span><strong>{dbCount}</strong></div>
                  <div><span>Estado</span><strong>{statusLabel(form.status)}</strong></div>
                  {instance?.source_instance_name && <div><span>Origen</span><strong>{instance.source_instance_name}</strong></div>}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'motor' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Configuración del motor</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Nombre de la instancia</label>
                    <input name="name" value={form.name} onChange={handleChange} className="form-control" placeholder="p. ej. odoo-prod, SRPCINT00\\PROD" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Servicio (opcional)</label>
                    <select name="service_id" value={form.service_id} onChange={handleChange} className="form-select">
                      <option value="">Sin servicio (solo máquina)</option>
                      {services.map(s => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Motor de base de datos</label>
                    <select name="dbms" value={form.dbms} onChange={handleChange} className="form-select">
                      {(meta.dbms_types || ['postgres','mysql','mssql','oracle','mongodb','redis']).map(t =>
                        <option key={t} value={t}>{t}</option>
                      )}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Versión</label>
                    <input name="version" value={form.version} onChange={handleChange} className="form-control" placeholder="15.3" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Puerto</label>
                    <input name="port" type="number" value={form.port} onChange={handleChange} className="form-control" placeholder="5432" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Rol</label>
                    <select name="role" value={form.role} onChange={handleChange} className="form-select">
                      <option value="">—</option>
                      {(meta.db_roles || ['primary','replica','standalone','witness']).map(r =>
                        <option key={r} value={r}>{roleLabel(r)}</option>
                      )}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Estado</label>
                    <select name="status" value={form.status} onChange={handleChange} className="form-select">
                      {(meta.db_statuses || ['online','offline','maintenance']).map(s =>
                        <option key={s} value={s}>{statusLabel(s)}</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {(isCards ? activeTab === 'resumen' : activeTab === 'host') && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Host y almacenamiento</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Servidor físico</label>
                    <CiSelector
                      ciType="rack_element"
                      ciId={form.host_server_id}
                      companyId={companyId}
                      fixedType="rack_element"
                      onSelect={handleHostServerSelect}
                      onClear={() => { setForm(prev => ({ ...prev, host_server_id: '' })); setIsDirty(true); }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Máquina virtual</label>
                    <CiSelector
                      ciType="virt_vm"
                      ciId={form.host_vm_id}
                      companyId={companyId}
                      fixedType="virt_vm"
                      onSelect={handleHostVmSelect}
                      onClear={() => { setForm(prev => ({ ...prev, host_vm_id: '' })); setIsDirty(true); }}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Directorio de datos</label>
                    <input name="data_dir" value={form.data_dir} onChange={handleChange} className="form-control" placeholder="/var/lib/postgresql/data" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Método de copia / clonado</label>
                    <input name="copy_method" value={form.copy_method} onChange={handleChange} className="form-control" placeholder="pg_dump, snapshot, Veeam…" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Notas</label>
                    <textarea name="notes" value={form.notes} onChange={handleChange} className="form-control" rows={3} placeholder="Notas operativas de la instancia…" />
                  </div>
                </div>
              </div>

              <div className="dv-section">
                <div className="dv-section-title">Replicación</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Replica hacia (destino)</label>
                    <input name="replication_target" value={form.replication_target} onChange={handleChange} className="form-control" placeholder="srvadb01, cluster-replica…" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Configuración especial</label>
                    <textarea name="replication_config" value={form.replication_config} onChange={handleChange} className="form-control" rows={3} placeholder="Streaming replication, slots…" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Scripts de replicación</label>
                    <textarea name="replication_scripts" value={form.replication_scripts} onChange={handleChange} className="form-control" rows={3} placeholder="Ruta/comandos de los scripts de replicación…" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

          {activeTab === 'bases' && isEdit && (
            <div className={paneClass}>
            <InstanceDatabasesPanel
              instanceId={instance.id}
              companyId={companyId}
              meta={meta}
              onCountChange={setDbCount}
            />
            </div>
          )}

          {activeTab === 'dependencias' && isEdit && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Servicio y dependencias</div>
                <p className="dv-context-help">
                  Este mapa es el del servicio al que pertenece la instancia: de dónde depende y a qué afecta.
                </p>
                {form.service_id ? (
                  <>
                    <div className="dv-context-actions" style={{ marginBottom: 10 }}>
                      <Link to={`/services/designer?service=${form.service_id}`} className="dv-btn dv-btn-secondary">
                        Abrir diseñador ↗
                      </Link>
                    </div>
                    <ServiceOverviewMap
                      serviceId={Number(form.service_id)}
                      companyId={companyId}
                      onOpenService={(id) => navigate(`/services?destacar=${id}&abrir=1`)}
                    />
                  </>
                ) : (
                  <div className="dv-empty-state">Asocia la instancia a un servicio para ver sus dependencias.</div>
                )}
              </div>
            </div>
          )}
        </fieldset>
        <FormDirtyDock
          show={isCards && isDirty}
          saving={saving}
          onDiscard={handleDiscard}
          onSave={() => handleSave()}
        />
        </FormCardsShell>
        </div>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="database"
          ciId={instance?.id}
          tipoLegible="la instancia"
        />
      </div>
    </div>
  );
}
